import React, { useEffect, useState } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';
import { useSessionLogoutSubscription } from '../../account/localLogout';
import { IS_APPLE } from '../../constants/os';
import { AllGroupsProvider } from '../../groups/useAllGroups';
import { AllSlimPostsProvider } from '../../post/useAllSlimPosts';
import { AllSeriesProvider } from '../../series/useAllSeries';
import { AllTopicsProvider } from '../../topic/useAllTopics';
import { UIActions } from '../../ui/uiSlice';
import { AllUsersProvider } from '../../user/useAllUsers';
import BeforeLeaveConfirmContextProvider from '../../utils/BeforeLeaveConfirm';
import { isElectron } from '../../utils/desktopClient';
import { MainContentHistoryProvider } from '../../utils/router/useMainContentHistory';
import useNavigate from '../../utils/router/useNavigate';
import useSlug from '../../utils/router/useSlug';
import { createPostPath } from '../../utils/url/postPath';
import { IsPublicViewProvider } from '../../utils/useIsPublicView';
import usePreferDesktopNotice from '../../utils/usePreferDesktopNotice';
import useShortcut from '../../utils/useShortcut';
import useVisibilityChangeDetection from '../../utils/useVisibilityChangeDetection';
import { useVersionCheck } from '../checkVersion';
import { useAppDispatch } from '../redux/useAppSelector';
import EnsureAdmin from '../router/EnsureAdmin';
import EnsureAnonymous from '../router/EnsureAnonymous';
import EnsureUser from '../router/EnsureUser';
import EnsureUserOrAnonymous from '../router/EnsureUserOrAnonymous';
import useRouteHandle from '../useRouteHandle';
import GoogleAnalytics from './GoogleAnalytics';
import SidebarShiftStateProvider from './SidebarShiftStateProvider';

const RENDER_OP = 'ui.react.render';

const now = () => Date.now() / 1000;

const useRouteChangeProfiler = (routeName: string) => {
  const [currentRender, setCurrentRender] = useState<{
    startTime: number;
    routeName: string;
  }>(() => ({
    startTime: now(),
    routeName,
  }));

  if (window.SentryTracing && currentRender.routeName !== routeName) {
    setCurrentRender({
      startTime: now(),
      routeName,
    });
  }

  useEffect(() => {
    if (!window.SentryTracing) return;

    // We start and end the span immediately in useEffect so
    // that it always gets attached to the "navigation" root span
    window.SentryTracing.startInactiveSpan({
      name: `Render /${currentRender.routeName}`,
      onlyIfParent: true,
      startTime: currentRender.startTime,
      op: RENDER_OP,
      attributes: {
        [window.SentryTracing.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]:
          'auto.ui.react.profiler',
        'ui.route_name': currentRender.routeName,
      },
    }).end(now());
  }, [currentRender]);
};

const ROLE_WRAPPER = {
  user: EnsureUser,
  admin: EnsureAdmin,
  anonymous: EnsureAnonymous,
  userOrAnonymous: EnsureUserOrAnonymous,
};

const AppRoot = () => {
  const [topicId] = useSlug('topicSlug');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const copyLinkKey = `${IS_APPLE ? 'command' : 'ctrl'}+l`;

  useShortcut('shift+n', (e) => {
    e.preventDefault();
    navigate(createPostPath({ topicId: topicId || undefined }));
  });

  useShortcut(copyLinkKey, async (e) => {
    if (isElectron) {
      e.preventDefault();

      await navigator.clipboard.writeText(window.location.href);

      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Link copied!',
        }),
      );
    }
  });

  useVersionCheck();
  usePreferDesktopNotice();
  useSessionLogoutSubscription();
  useVisibilityChangeDetection();

  const location = useLocation();
  const { routeName, pageLayout = {}, ensureRole } = useRouteHandle();

  useRouteChangeProfiler(routeName);

  useEffect(() => {
    if (routeName) {
      analytics.page(routeName);
      window.Intercom?.('update');
    }
  }, [routeName, location]);

  const EnsureWrapper =
    (ensureRole && ROLE_WRAPPER[ensureRole]) || React.Fragment;

  return (
    <AllUsersProvider>
      <AllGroupsProvider>
        <AllTopicsProvider>
          <AllSeriesProvider>
            <AllSlimPostsProvider>
              <BeforeLeaveConfirmContextProvider>
                <MainContentHistoryProvider skipLocation={!pageLayout.sidebar}>
                  <ScrollRestoration />
                  <EnsureWrapper>
                    <SidebarShiftStateProvider>
                      <Outlet />
                    </SidebarShiftStateProvider>
                  </EnsureWrapper>
                  <GoogleAnalytics />
                </MainContentHistoryProvider>
              </BeforeLeaveConfirmContextProvider>
            </AllSlimPostsProvider>
          </AllSeriesProvider>
        </AllTopicsProvider>
      </AllGroupsProvider>
    </AllUsersProvider>
  );
};

export default () => (
  <IsPublicViewProvider>
    <AppRoot />
  </IsPublicViewProvider>
);
